import type { Locale } from '@/shared/types/api';

/**
 * DS-04 — the language a piece of text is written in, so a name in the other
 * language can say so (`lang`, WCAG 3.1.2).
 *
 * An Arabic name on an English page left unmarked falls into the Latin font
 * stack, where some machines draw it unjoined; `index.css` sets a fragment
 * marked `lang="ar"` in the Arabic face. An English name on an Arabic page is
 * marked `lang="en"` so it is read with an English voice.
 *
 * Decided by script only, never by guessing words: Arabic letters, Latin
 * letters, or neither. Text with no letters (a date, an amount, a number) and
 * text that mixes both scripts claim no language and are left alone.
 */

// The Arabic blocks: Arabic, Arabic Supplement, Arabic Extended-A and the presentation forms.
const ARABIC = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFC]/u;
// Basic and extended Latin letters.
const LATIN = /[A-Za-z\u00C0-\u024F]/u;

/** `ar` for Arabic letters, `en` for Latin ones, `null` for neither or both. */
export function writtenIn(text: string | null | undefined): Locale | null {
  if (!text) return null;

  const arabic = ARABIC.test(text);
  const latin = LATIN.test(text);

  if (arabic === latin) return null;

  return arabic ? 'ar' : 'en';
}

/** The `lang` a piece of text needs on a page in `page`: its own, when that differs; else none. */
export function foreignLang(text: string | null | undefined, page: Locale): Locale | undefined {
  const own = writtenIn(text);

  return own !== null && own !== page ? own : undefined;
}

/**
 * How a shared component is told that a value is a name (DS-04): `'auto'`, its
 * language is read from its text; `'ar'` or `'en'`, it is stated. Either way
 * it is marked only on a page in the other language.
 */
export type TextLang = 'auto' | Locale;

/** The `lang` for a value declared as `declared`, on a page in `page`. */
export function langFor(
  declared: TextLang | undefined,
  value: unknown,
  page: Locale,
): Locale | undefined {
  if (declared === undefined) return undefined;
  if (declared !== 'auto') return declared === page ? undefined : declared;

  return typeof value === 'string' ? foreignLang(value, page) : undefined;
}
